import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { EventCard } from '../components/EventCard';
import type { Event, EventType } from '@wineryxplorer/shared';
import { EventFilters, EventsPagination } from '../components/Events';
import { useSearchScope } from '../hooks/useSearchScope';
import { RegionSearch } from '../components/SearchPage/RegionSearch';
import type { Region, CityLocation } from '../components/SearchPage/RegionSearch';
import { MapPanel, SearchAreaButton } from '../components/SearchPage';
import { SearchResultsWithMap } from '../components/SearchResultsWithMap';
import { SearchScopePill } from '../components/search/SearchScopePill';

// Shape the event.list venue field exposes (same on geo + non-geo path)
type EventVenue = {
  _id: string;
  name: string;
  address?: {
    city?: string;
    state?: string;
    coordinates?: {
      type?: string;
      coordinates?: [number, number]; // [lng, lat] GeoJSON
    };
  };
};

// Shape consumed by MapPanel (only the fields Events actually provides)
type MapVenue = {
  _id: string;
  name: string;
  venueType: string;
  address?: {
    city?: string;
    state?: string;
    coordinates?: { lat: number; lng: number } | null;
  };
};

export const Events = () => {
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [eventType, setEventType] = useState<EventType | undefined>(undefined);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [venueId, setVenueId] = useState<string>('');
  const [highlightedVenueId, setHighlightedVenueId] = useState<string | null>(null);

  // Search scope (region / city / geolocation / map bounds)
  const {
    selectedRegion,
    selectedCity,
    mapBounds,
    pendingMapBounds,
    locationParam,
    boundsParam,
    hasUncommittedPan,
    scopePill,
    mapCenter,
    mapZoom,
    isLocating,
    geoError,
    handleRegionChange,
    handleCitySearch,
    handleUseMyLocation,
    handleMapBoundsChanged,
    handleCommitPendingBounds,
  } = useSearchScope();

  // Reset to offset 0 when scope changes. Mirrors page reset in SearchPage/Experiences.
  // userLocation is intentionally excluded — GPS floats vary per-read and would cause
  // spurious resets mid-browse. Offset is reset explicitly in onUseMyLocation instead.
  const prevScopeIdRef = useRef<string>(
    `${selectedRegion?.slug ?? ''}|${selectedCity?.name ?? ''}|${mapBounds ? `${mapBounds.ne.lat},${mapBounds.ne.lng},${mapBounds.sw.lat},${mapBounds.sw.lng}` : ''}`
  );
  useEffect(() => {
    const currentId = `${selectedRegion?.slug ?? ''}|${selectedCity?.name ?? ''}|${mapBounds ? `${mapBounds.ne.lat},${mapBounds.ne.lng},${mapBounds.sw.lat},${mapBounds.sw.lng}` : ''}`;
    if (prevScopeIdRef.current !== currentId) {
      prevScopeIdRef.current = currentId;
      setOffset(0);
    }
  }, [selectedRegion, selectedCity, mapBounds]);

  // Wraps handleUseMyLocation to reset pagination on explicit "Use my location" click
  const onUseMyLocation = useCallback(() => {
    handleUseMyLocation(() => {
      setOffset(0);
    });
  }, [handleUseMyLocation]);

  // Fetch venues for the filter dropdown
  const { data: venuesData } = trpc.venue.list.useQuery({
    limit: 100,
    offset: 0,
  });

  const { data, isLoading, error } = trpc.event.list.useQuery({
    limit,
    offset,
    eventType,
    startDate: startDate.length > 0 ? startDate : undefined,
    endDate: endDate.length > 0 ? endDate : undefined,
    venueId: venueId.length > 0 ? venueId : undefined,
    location: locationParam,
    bounds: boundsParam,
  });

  // Derive deduped venue pins from event results
  const mapVenues = useMemo<MapVenue[]>(() => {
    if (!data) return [];
    const byVenueId = new Map<string, MapVenue>();
    for (const event of data.items) {
      const v = (event as { venue?: EventVenue | null }).venue;
      if (!v?._id || !v.name) continue;
      if (byVenueId.has(v._id)) continue;
      // GeoJSON Point: coordinates is [lng, lat]
      const geoCoords = v.address?.coordinates?.coordinates;
      const coordinates = Array.isArray(geoCoords)
        ? { lat: geoCoords[1], lng: geoCoords[0] }
        : null;
      byVenueId.set(v._id, {
        _id: v._id,
        name: v.name,
        venueType: 'winery',
        address: {
          city: v.address?.city,
          state: v.address?.state,
          coordinates,
        },
      });
    }
    return Array.from(byVenueId.values());
  }, [data]);

  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-6xl mx-auto">
        <PageContainer>
          <div className="text-center py-8">
            <p className="text-gray-600">Loading events...</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-6xl mx-auto">
        <PageContainer>
          <div className="text-center py-8">
            <p className="text-red-600">Error loading events: {error.message}</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const events = data.items as Array<
    Event & {
      venue?: {
        name: string;
        address?: {
          city?: string;
          state?: string;
        };
      };
    }
  >;
  const total = data.total;

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const handlePreviousPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setEventType(value === '' ? undefined : (value as EventType));
    setOffset(0); // Reset to first page when filter changes
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setOffset(0);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setOffset(0);
  };

  const handleVenueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setVenueId(e.target.value);
    setOffset(0);
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  const venues = venuesData?.items || [];

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-6xl mx-auto">
      <PageContainer className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Events</h1>
          <p className="text-gray-600">Discover upcoming wine events and experiences</p>
        </div>

        {/* Region / location search */}
        <div>
          <RegionSearch
            selectedRegion={selectedRegion}
            selectedCity={selectedCity}
            onRegionChange={(region: Region | null) => handleRegionChange(region)}
            onCitySearch={(city: CityLocation) => handleCitySearch(city)}
            onUseMyLocation={onUseMyLocation}
            isLocating={isLocating}
            geoError={geoError}
          />
        </div>

        {/* Active-scope pill */}
        {scopePill && (
          <div>
            <SearchScopePill label={scopePill.label} onDismiss={scopePill.onDismiss} />
          </div>
        )}

        {/* Filters */}
        <EventFilters
          eventType={eventType}
          venueId={venueId}
          startDate={startDate}
          endDate={endDate}
          venues={venues}
          onEventTypeChange={handleEventTypeChange}
          onVenueChange={handleVenueChange}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onClearEventType={() => {
            setEventType(undefined);
            setOffset(0);
          }}
          onClearVenue={() => {
            setVenueId('');
            setOffset(0);
          }}
          onClearDateRange={clearDateRange}
        />

        {/* Events list + map (two-column on desktop when scope is set, list-only otherwise) */}
        {locationParam ? (
          <SearchResultsWithMap
            map={
              <MapPanel
                venues={mapVenues}
                onBoundsChanged={handleMapBoundsChanged}
                highlightedVenueId={highlightedVenueId}
                onPinClick={setHighlightedVenueId}
                center={mapCenter}
                zoom={mapZoom}
                infoWindowLinkTo={(venueId) => `/events?venueId=${venueId}`}
                topCenterOverlay={
                  hasUncommittedPan && pendingMapBounds ? (
                    <SearchAreaButton
                      bounds={pendingMapBounds}
                      filters={{}}
                      onCommit={handleCommitPendingBounds}
                    />
                  ) : null
                }
              />
            }
          >
            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No events found in this area. Try adjusting filters or panning the map.
              </div>
            )}
          </SearchResultsWithMap>
        ) : (
          <>
            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No events found. Check back soon for new events!</p>
              </div>
            )}
          </>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <EventsPagination
            offset={offset}
            limit={limit}
            total={total}
            currentPage={currentPage}
            totalPages={totalPages}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
          />
        )}
      </PageContainer>
    </div>
  );
};
